import axios from 'axios';
import { CONFIG } from 'src/config-global';
import { isPermissionError } from 'src/utils/permission-helper';
// import {environment} from '../environment/environment';

class Services {
  // Track if init has been called to prevent duplicate interceptors
  private static isInitialized = false;

  // Call at the very beginning
  static init(): void {
    // Prevent duplicate initialization - make it idempotent
    if (Services.isInitialized) {
      return;
    }

    // Set axios defaults (idempotent - safe to call multiple times)
    axios.defaults.timeout = CONFIG.reqTimeout;
    axios.defaults.headers.common['Content-Type'] = 'application/json';
    axios.defaults.baseURL = CONFIG.baseUrl;

    // Add response interceptor to handle 403 permission errors
    // IMPORTANT: Only redirect for permission-related errors, NOT authentication errors
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if it's a 403 error
        if (error?.response?.status === 403) {
          const errorData = error.response.data;
          const errorMessage = errorData?.errMessage || '';
          const requestUrl = error?.config?.url || '';
          
          // Skip redirect for authentication/login endpoints - these are auth errors, not permission errors
          const isAuthEndpoint = requestUrl.includes('/auth/login') || 
                                 requestUrl.includes('/auth/admin/login') ||
                                 requestUrl.includes('/auth/sign-in');
          
          if (isAuthEndpoint) {
            // Let authentication errors be handled by the login component
            return Promise.reject((error.response && error.response.data) || error);
          }
          
          // Only redirect for permission-related errors (not authentication/account status errors)
          if (isPermissionError(error)) {
            // Redirect to 403 page immediately - use replace to prevent back button issues
            if (typeof window !== 'undefined') {
              window.location.replace('/403');
            }
            // Return a rejected promise to stop further error handling
            return Promise.reject(new Error('Permission denied - redirecting to 403 page'));
          }
        }
        // For other errors (including auth 403s), use default handling
        return Promise.reject((error.response && error.response.data) || error);
      }
    );

    // Mark as initialized to prevent duplicate calls
    Services.isInitialized = true;
  }

  // Call after authentication is done and make required changes
  static initAfterAuth(config: { token: string }): void {
    // eslint-disable-next-line @typescript-eslint/dot-notation
    axios.defaults.headers.common['authorization'] = `Bearer ${config?.token || ''}`;
  }

  // The generic handling of errors
  static genericErrorHandler(error: any): void {
    if (error && error.response && error.response.data) {
      throw error.response.data;
    } else {
      throw error;
    }
  }
}

export default Services;
